import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { FadeUp, SectionTag } from "../components/site/Reveal";

const MODULES = [
  { code: "MOD-01", title: "Lead Capture", weeks: "Week 1", desc: "One intake channel for every lead, instant auto-replies, and a master lead log you review in 10 minutes a week.", artifact: "Master lead log + auto-reply scripts" },
  { code: "MOD-02", title: "Estimates", weeks: "Week 2", desc: "A pricing matrix built from your real costs, and an estimate template that turns site visits into same-day quotes.", artifact: "Pricing matrix + estimate template" },
  { code: "MOD-03", title: "Scheduling", weeks: "Week 3", desc: "An ideal-week calendar with confirmation and reminder sequences that cut no-shows and end double-booking.", artifact: "Booking calendar + reminder sequence" },
  { code: "MOD-04", title: "Customer Records", weeks: "Week 4", desc: "One clean record per customer — contacts, property notes, job history, preferences — kept tidy in minutes a week.", artifact: "Customer record system" },
  { code: "MOD-05", title: "Invoicing", weeks: "Week 5", desc: "Same-day invoicing as a habit, with templates, terms, and a polite-but-firm overdue sequence.", artifact: "Invoice template + overdue sequence" },
  { code: "MOD-06", title: "Payments", weeks: "Week 6", desc: "Card and bank collection on every invoice, deposits that protect your calendar, and a 15-minute Friday money routine.", artifact: "Payment links + reconciliation checklist" },
  { code: "MOD-07", title: "Follow-Up", weeks: "Week 7", desc: "Post-job follow-ups, review requests that get answered, and seasonal rebook reminders that run themselves.", artifact: "Follow-up engine + rebook calendar" },
];

const INCLUDED = [
  "7 guided implementation modules (one per week)",
  "Every template, script, checklist, and calculator",
  "Weekly live cohort call + office hours",
  "Direct feedback on your actual systems",
  "Accountability check-ins so it actually gets built",
  "WorkBase Hub access — modules, vault, dashboard",
];

const NOT_INCLUDED = [
  "Done-for-you setup — you build it, we guide",
  "Generic business coaching or mindset content",
  "A software subscription you're locked into",
  "Marketing or lead-generation services",
];

export default function Program() {
  return (
    <div data-testid="program-page" className="bg-navy pt-16">
      <section className="relative blueprint-grid grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <FadeUp>
            <SectionTag dark>The Offer // Guided Implementation</SectionTag>
            <h1 className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.03] mt-6 max-w-4xl">
              The Back Office <span className="text-safety">Blueprint.</span>
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed mt-6 max-w-2xl">
              A 7-week guided implementation program for owner-operated service businesses.
              You bring the business and do the building. We bring the training, templates,
              workflows, coaching, and accountability.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="bg-bone text-navy blueprint-grid-light">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <FadeUp className="mb-14">
            <SectionTag>The Curriculum // Week by Week</SectionTag>
            <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl mt-5">
              Seven modules. Seven working systems.
            </h2>
          </FadeUp>
          <div className="space-y-px bg-slate-300 border border-slate-300">
            {MODULES.map((m, i) => (
              <FadeUp key={m.code} delay={i * 0.04}>
                <div className="bg-bone hover:bg-white transition-colors p-6 sm:p-8 grid md:grid-cols-12 gap-4 items-start" data-testid={`module-row-${m.code.toLowerCase()}`}>
                  <div className="md:col-span-2">
                    <p className="font-mono text-[11px] tracking-[0.2em] text-safety">[{m.code}]</p>
                    <p className="font-mono text-[11px] text-slate-500 mt-1 uppercase">{m.weeks}</p>
                  </div>
                  <div className="md:col-span-6">
                    <h3 className="font-display font-bold text-xl">{m.title}</h3>
                    <p className="text-slate-600 text-sm mt-2 leading-relaxed">{m.desc}</p>
                  </div>
                  <div className="md:col-span-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">You install</p>
                    <p className="text-sm font-semibold">{m.artifact}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy grain relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid md:grid-cols-2 gap-6">
          <FadeUp>
            <div className="bg-charcoal border border-white/10 p-8 h-full">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400 mb-6">[INCLUDED]</p>
              <ul className="space-y-4">
                {INCLUDED.map((i) => (
                  <li key={i} className="flex gap-3 text-slate-200 text-sm leading-relaxed">
                    <Check size={17} className="text-emerald-400 shrink-0 mt-0.5" />{i}
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="bg-charcoal border border-white/10 p-8 h-full">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 mb-6">[WHAT IT IS NOT]</p>
              <ul className="space-y-4">
                {NOT_INCLUDED.map((i) => (
                  <li key={i} className="flex gap-3 text-slate-400 text-sm leading-relaxed">
                    <X size={17} className="text-slate-600 shrink-0 mt-0.5" />{i}
                  </li>
                ))}
              </ul>
              <p className="text-slate-500 text-sm mt-8 pt-6 border-t border-white/10 leading-relaxed">
                The point is ownership. When we're done, the system is yours — no
                dependency on us, no software lock-in, ready for your first hire.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="bg-bone text-navy blueprint-grid-light">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <FadeUp className="lg:col-span-7">
              <SectionTag>Enrollment // Next Cohort</SectionTag>
              <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl mt-5">
                Founding Cohort pricing. Lifetime access to what you build.
              </h2>
              <p className="text-slate-600 mt-5 leading-relaxed max-w-xl">
                Enrollment is by application. We accept operators we know we can help —
                if it's not a fit, we'll tell you straight and point you somewhere useful.
              </p>
              <div className="mt-8 space-y-3 text-sm text-slate-700">
                <p className="flex gap-3"><Check size={16} className="text-safety shrink-0 mt-0.5" />Application reviewed by hand within 2 business days</p>
                <p className="flex gap-3"><Check size={16} className="text-safety shrink-0 mt-0.5" />Secure checkout — pay only after you're accepted</p>
                <p className="flex gap-3"><Check size={16} className="text-safety shrink-0 mt-0.5" />14-day fit guarantee — see the refund policy</p>
              </div>
            </FadeUp>
            <FadeUp delay={0.15} className="lg:col-span-5">
              <div className="bg-navy text-white p-8 sm:p-10 grain relative" data-testid="pricing-card">
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">[The Back Office Blueprint // Founding Cohort]</p>
                <div className="flex items-end gap-2 mt-6">
                  <span className="font-display font-extrabold text-6xl" data-testid="program-price">$497</span>
                  <span className="text-slate-400 text-sm mb-2">paid in full</span>
                </div>
                <p className="font-mono text-sm text-safety mt-2" data-testid="program-price-plan">or 2 payments of $275</p>
                <div className="perforation h-3 -mx-8 sm:-mx-10 mt-6" />
                <ul className="mt-6 space-y-2.5 text-sm text-slate-300">
                  <li>7-week guided implementation</li>
                  <li>Full template vault</li>
                  <li>Weekly coaching calls</li>
                  <li>WorkBase Hub member access</li>
                </ul>
                <Link
                  to="/apply"
                  data-testid="pricing-apply-btn"
                  className="mt-8 bg-safety hover:bg-safety-hover text-white font-semibold px-6 py-4 flex items-center justify-center gap-2 transition-colors w-full"
                >
                  Apply for the Founding Cohort <ArrowRight size={16} />
                </Link>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-4 text-center">
                  No payment is required to apply. Accepted applicants receive enrollment instructions.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
}
